/**
 * How an add-on is charged, pure (batch-29, item R1; plan §3.4). Each add-on is per person, per room or per booking,
 * and per night or per stay. An add-on saved before these keys existed is read through this one definition — the
 * database has the same one as `item_basis(item)` (item R2) — so older events charge exactly what they charge today.
 * The server's arithmetic is the one that counts; this mirror is for the page.
 */
export type Basis = 'person' | 'room' | 'booking';
export type Per = 'night' | 'stay';
export interface BasisItem {
  price_cents: number;
  required?: boolean;
  per_guest?: boolean;
  basis?: Basis | null;
  per?: Per | null;
  /** a price per age-group key, for per-person add-ons; a group that is missing pays `price_cents` */
  group_prices?: Record<string, number> | null;
}

const BASES: readonly string[] = ['person', 'room', 'booking'];

/** The basis an add-on is charged on. Missing keys: required or per-guest reads as per person per stay, anything else as per booking per stay. */
export function itemBasis(item: BasisItem): { basis: Basis; per: Per } {
  const basis: Basis = item.basis && BASES.includes(item.basis) ? item.basis : item.required || item.per_guest ? 'person' : 'booking';
  return { basis, per: item.per === 'night' ? 'night' : 'stay' };
}

/** What one person of a group pays for a per-person add-on. */
export const groupPrice = (item: BasisItem, group: string): number => item.group_prices?.[group] ?? item.price_cents;

export interface LineInput {
  /** per person: how many of each age group take it */
  people?: Record<string, number>;
  /** per room: how many rooms take it */
  rooms?: number;
  /** per booking: the quantity */
  quantity?: number;
  nights: number;
}

/** The add-on's line total in cents. */
export function itemLineCents(item: BasisItem, input: LineInput): number {
  const { basis, per } = itemBasis(item);
  const times = per === 'night' ? input.nights : 1;
  if (basis === 'person') return Object.entries(input.people ?? {}).reduce((sum, [group, n]) => sum + n * groupPrice(item, group), 0) * times;
  if (basis === 'room') return (input.rooms ?? 0) * item.price_cents * times;
  return (input.quantity ?? 0) * item.price_cents;
}
